import React from 'react';
import { Loader2 } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const variantStyles: Record<Variant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  ghost: 'btn-ghost',
  danger: 'btn-danger',
};

const sizeStyles: Record<Size, string> = {
  sm: 'btn-sm',
  md: 'btn-md',
  lg: 'btn-lg',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'left',
  children,
  disabled,
  className = '',
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;

  const { onClick, ...rest } = props;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isDisabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    e.currentTarget.style.setProperty('--ripple-x', `${x}px`);
    e.currentTarget.style.setProperty('--ripple-y', `${y}px`);
    onClick?.(e);
  };

  return (
    <>
      <style>{`
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-family: 'Satoshi', system-ui, sans-serif;
          font-weight: 600;
          border: 1px solid transparent;
          border-radius: var(--radius-interactive);
          cursor: pointer;
          transition: transform 100ms var(--spring-easing), background var(--spring-duration) var(--spring-easing), border-color var(--spring-duration) var(--spring-easing), box-shadow var(--spring-duration) var(--spring-easing);
          white-space: nowrap;
          user-select: none;
          position: relative;
          overflow: hidden;
          letter-spacing: 0.01em;
        }
        .btn:active:not(:disabled) { transform: scale(var(--press-scale)); }
        .btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .btn-primary {
          background: var(--gradient-gemini);
          color: #fff;
          border: none;
          box-shadow: var(--shadow-glow);
          border-radius: var(--radius-interactive);
          transition: all var(--spring-duration) var(--spring-easing);
        }
        .btn-primary:hover:not(:disabled) {
          filter: brightness(1.1);
          box-shadow: var(--shadow-glow-strong);
          transform: scale(1.02);
        }
        .btn-secondary {
          background: var(--glass-bg-card);
          backdrop-filter: blur(8px) saturate(180%);
          -webkit-backdrop-filter: blur(8px) saturate(180%);
          color: var(--color-text);
          border: var(--glass-border);
          border-radius: var(--radius-interactive);
          transition: all var(--spring-duration) var(--spring-easing);
        }
        .btn-secondary:hover:not(:disabled) {
          background: rgba(62, 145, 255, 0.10);
          border-color: rgba(62, 145, 255, 0.30);
        }
        .btn-ghost {
          background: transparent;
          color: var(--color-text-muted);
          border-color: transparent;
          border-radius: var(--radius-interactive);
          transition: all var(--spring-duration) var(--spring-easing);
        }
        .btn-ghost:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.05);
          color: var(--color-text);
        }
        .btn-danger {
          background: rgba(239, 68, 68, 0.10);
          border: 0.5px solid rgba(239, 68, 68, 0.25);
          color: var(--color-error);
          border-radius: var(--radius-interactive);
          transition: all var(--spring-duration) var(--spring-easing);
        }
        .btn-danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.20);
          border-color: rgba(239, 68, 68, 0.40);
        }
        .btn-sm { font-size: var(--text-xs); padding: 0.375rem 0.75rem; min-height: 32px; font-weight: 500; }
        .btn-md { font-size: var(--text-sm); padding: 0.4375rem 1rem; height: 38px; font-weight: 600; }
        .btn-lg { font-size: var(--text-base); padding: 0.5625rem 1.5rem; height: 46px; font-weight: 600; }
        [data-theme="light"] .btn-primary {
          color: #fff;
        }
        [data-theme="light"] .btn-secondary {
          background: rgba(0, 0, 0, 0.04);
          border: 0.5px solid rgba(0, 0, 0, 0.10);
          color: var(--color-text);
        }
        [data-theme="light"] .btn-secondary:hover:not(:disabled) {
          background: rgba(62, 145, 255, 0.08);
          border-color: rgba(62, 145, 255, 0.25);
        }
        [data-theme="light"] .btn-ghost {
          color: var(--color-text-muted);
        }
        [data-theme="light"] .btn-ghost:hover:not(:disabled) {
          background: rgba(0, 0, 0, 0.04);
        }
        [data-theme="light"] .btn-danger {
          background: rgba(220, 38, 38, 0.08);
          border: 0.5px solid rgba(220, 38, 38, 0.20);
        }
        [data-theme="light"] .btn-danger:hover:not(:disabled) {
          background: rgba(220, 38, 38, 0.15);
        }
      `}</style>
      <button
        className={`btn ripple-container ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
        disabled={isDisabled}
        onClick={handleClick}
        {...rest}
      >
        {loading && <Loader2 size={size === 'sm' ? 14 : size === 'lg' ? 18 : 15} className="spin" style={{ animation: 'spin 1s linear infinite' }} />}
        {!loading && icon && iconPosition === 'left' && icon}
        {children}
        {!loading && icon && iconPosition === 'right' && icon}
      </button>
    </>
  );
}
